import { Info } from "lucide-react";
import React, { ReactNode } from "react";

import { ListItemButton, Stack, StackProps } from "@mui/material";
import { styled } from "@mui/material/styles";

import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";

const PREFIX = "IgCardSelect";

const classes = {
  root: `${PREFIX}-root`,
};

const StyledStack = styled(Stack)(({ theme }) => ({
  [`& .${classes.root}`]: {
    borderRadius: "6px",
    border: `1px solid ${theme.igBorders.core.default}`,
    "&:hover, &:focus": {
      border: `1px solid ${theme.igBorders.core.hover}`,
    },
  },
}));

type IgCardSelectOption<T extends string | null> = {
  description?: ReactNode;
  disabled?: boolean;
  key?: string;
  title: ReactNode;
  tooltip?: ReactNode;
  value: T;
};

type IgCardSelectProps<T extends string | null> = Omit<
  StackProps,
  "children" | "onChange"
> & {
  getOptionSelected?: (
    option: IgCardSelectOption<T>,
    selectedValue: T
  ) => boolean;
  onChange: (value: T) => void;
  options: IgCardSelectOption<T>[];
  value: T;
};

export const IgCardSelect = <T extends string | null>({
  getOptionSelected,
  onChange,
  options,
  value,
  ...stackProps
}: IgCardSelectProps<T>) => {
  return (
    <StyledStack spacing={2} direction={"column"} {...stackProps}>
      {options.map((option) => {
        const selected = getOptionSelected
          ? getOptionSelected(option, value)
          : option.value === value;

        return (
          <ListItemButton
            key={option.key ?? String(option.value)}
            classes={{ root: classes.root }}
            disabled={option.disabled}
            onClick={() => {
              onChange(option.value);
            }}
            selected={selected}
          >
            <Stack direction={"column"} flex={1}>
              <IgTypography sx={{ fontWeight: 500 }}>
                {option.title}
                {option.tooltip && (
                  <IgTooltip title={option.tooltip} placement="right">
                    <Info size={16} style={{ marginLeft: 4 }} />
                  </IgTooltip>
                )}
              </IgTypography>
              {option.description && (
                <IgTypography variant={"body2"}>
                  {option.description}
                </IgTypography>
              )}
            </Stack>
          </ListItemButton>
        );
      })}
    </StyledStack>
  );
};
